import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  CommandStatus,
  EditMeterDataIn,
  GuidDictionaryItem,
  MeterAutentification,
  MeterPhase,
  MeterType,
  GsmProviders,
  RatioDataIn,
  IEditMeterDataIn,
  ParentMeterDataOut,
  GuidParentMeterDataOutDictionaryItem,
  MeterParentGetDataIn,
  ResponseErrorCode
} from '../../../../../api/api-reference';
import { DatepickerComponent } from '../../../../core/components/datepicker/datepicker.component';
import { DateRangeInput } from '../../../../core/models/date-range-input';
import { Ratio } from '../../../model/ratio';
import { Observable, Subscription } from 'rxjs';
import { TimeZoneService } from '../../../../../services/timezone.service';
import { formatDateDDMMMYYYY, toUtcDate } from '../../../../../helper/date-utils';
import { DropdownSearchComponent } from '../../../components/parent-meter-dropdown-search/parent-meter-dropdown-search.component';

@Component({
  selector: 'app-device-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ReactiveFormsModule, DatepickerComponent, DropdownSearchComponent],
  providers: [DatePipe],
  templateUrl: './device-edit.component.html',
  styleUrl: './device-edit.component.scss',
})
export class DeviceEditComponent implements OnInit {
  private readonly MIN_CLOCK_OFFSET = 30;

  selectedAssetType: string = '';
  selectedParent: ParentMeterDataOut | null = null;

  deviceForm: FormGroup = this.fb.group({
    assetType: ['', Validators.required],
    parent: [null],
    serviceLatitude: [null, [Validators.min(-90), Validators.max(90)]],
    serviceLongitude: [null, [Validators.min(-180), Validators.max(180)]],
    serviceName: ['', Validators.required],
    serviceNumber: [null],
    serviceType: [null],
    region: ['', Validators.required],
    area: ['', Validators.required],
    address: ['', Validators.required],
    meterNumber: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
    meterMake: ['', Validators.required],
    meterModel: ['', Validators.required],
    meterType: [null],
    meterPhase: [{ value: null, disabled: true }],
    currentTransformerRatioNumerator: [null, Validators.min(1)],
    currentTransformerRatioDenominator: [null, Validators.min(1)],
    voltageTransformerRatioNumerator: [null, Validators.min(1)],
    voltageTransformerRatioDenominator: [null, Validators.min(1)],
    gsmProvider: ['', Validators.required],
    cellularSimSerialNumber: ['', Validators.required],
    meterIp: ['', [Validators.required, Validators.pattern('^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$')]],
    meterPort: [null, [Validators.required, Validators.min(1024), Validators.max(49151)]],
    meterInterface: [{ value: '', disabled: true }],
    autentificationType: [{ value: '', disabled: true }],
    dataCollectionInterval: [null, Validators.required],
    clockOffset: [null, Validators.min(this.MIN_CLOCK_OFFSET)],
    meterTimeZone: [null, Validators.required],
    clientAddress: [{ value: '', disabled: true }],
    meterInstallationDate: [null, Validators.required],
    meterManufactureDate: [null],
    security: [{ value: '', disabled: true }],
    blockCipherKey: [null, Validators.maxLength(32)],
    authenticationKey: [null, Validators.maxLength(32)],
    password: [{ value: null, disabled: true }],
    newPassword: [{ value: null, disabled: true }, [Validators.pattern('^\\S{8}$')]],
    repeatNewPassword: [{ value: null, disabled: true }, [Validators.pattern('^\\S{8}$')]],
  });

  @Input() device: EditMeterDataIn = new EditMeterDataIn();
  @Input() embedded = false;

  @Output() closeSignal = new EventEmitter<boolean>();
  showPassword: { [key: string]: boolean } = {
    password: false,
    newPassword: false,
    repeatNewPassword: false,
  };
  changePasswordOnMeter = false;
  regions: GuidDictionaryItem[] = [];
  areas: GuidDictionaryItem[] = [];
  serviceTypes: GuidDictionaryItem[] = [];
  meterMakes: GuidDictionaryItem[] = [];
  meterModels: GuidDictionaryItem[] = [];
  assetTypes: GuidDictionaryItem[] = [];
  MeterAutentification = MeterAutentification;
  isDatepickerShown = false;
  isManufactureDatepickerShown = false;
  timezones = this.timezoneService.timezones;

  intervals = [
    { value: 15 },
    { value: 30 },
    { value: 60 },
    { value: 1440 },
  ];

  submitting: boolean = false;
  submitted: boolean = false;

  private subscriptions: Subscription[] = [];

  // Meter-data editing and password change are mutually exclusive; the first action taken wins.
  private readonly passwordControlNames = ['password', 'newPassword', 'repeatNewPassword'];
  private meterDataLocked = false;
  private lockedMeterDataControls: string[] = [];

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private datePipe: DatePipe,
    private fb: FormBuilder, private timezoneService: TimeZoneService
  ) { }

  meterTypeDictionary: MeterType[] = Object.keys(MeterType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => MeterType[key as keyof typeof MeterType]);

  meterPhaseDictionary: MeterPhase[] = Object.keys(MeterPhase)
    .filter((key) => isNaN(Number(key)))
    .map((key) => MeterPhase[key as keyof typeof MeterPhase]);

  gsmProviderDictionary: GsmProviders[] = Object.keys(GsmProviders)
    .filter((key) => isNaN(Number(key)))
    .map((key) => GsmProviders[key as keyof typeof GsmProviders]);

  ngOnInit() {
    if (this.device) {
      this.deviceForm.patchValue({
        assetType: this.device.assetType,
        serviceLatitude: this.device.serviceLatitude,
        serviceLongitude: this.device.serviceLongitude,
        serviceName: this.device.serviceName,
        serviceNumber: this.device.serviceNumber,
        serviceType: this.device.serviceType,
        region: this.device.regionId,
        area: this.device.areaId,
        address: this.device.address,
        meterNumber: this.device.meterNumber,
        meterMake: this.device.meterMake,
        meterModel: this.device.meterModel,
        meterType: this.device.meterType,
        currentTransformerRatioNumerator: this.device.currentTransformerRatio?.numerator,
        currentTransformerRatioDenominator: this.device.currentTransformerRatio?.denominator,
        voltageTransformerRatioNumerator: this.device.voltageTransformerRatio?.numerator,
        voltageTransformerRatioDenominator: this.device.voltageTransformerRatio?.denominator,
        gsmProvider: this.device.gsmProvider,
        cellularSimSerialNumber: this.device.cellularSimSerialNumber,
        meterIp: this.device.meterIp,
        meterPort: this.device.meterPort,
        dataCollectionInterval: this.device.dataCollectionInterval,
        clockOffset: this.device.clockTolerance != null && this.device.clockTolerance >= this.MIN_CLOCK_OFFSET ? this.device.clockTolerance : null,
        meterTimeZone: this.device.meterTimeZone,
        meterInstallationDate: this.device.meterInstallationDate,
        meterManufactureDate: this.device.meterManufactureDate,
        blockCipherKey: this.device.blockCipherKey,
        authenticationKey: this.device.authenticationKey,
        password: this.device.password,
        parent: this.device.parentId,
      });
    }

    this.getRegions();
    this.getServiceTypes();
    this.getAssetTypes();
    this.getMeterMakes();
    this.getMeterModels();
    this.getMeterModel();
    this.getAreas();
    this.getParent(this.device.parentId);
    this.selectedAssetType = this.device.assetType;

    const allProviders = Object.values(GsmProviders);

    if (allProviders.length === 1) {
      this.deviceForm.patchValue({ gsmProvider: allProviders[0] });
    }

    const subscription = this.deviceForm.valueChanges.subscribe(() => {
      const x = this.deviceForm.getRawValue();

      this.onAuthTypeChange();
      this.onNewPasswordChange();
      this.applyEditExclusivity();
      this.enableRequiredValidatorsForKeys(['currentTransformerRatioNumerator', 'currentTransformerRatioDenominator'], x.currentTransformerRatioNumerator != null || x.currentTransformerRatioDenominator != null);
      this.enableRequiredValidatorsForKeys(['voltageTransformerRatioNumerator', 'voltageTransformerRatioDenominator'], x.voltageTransformerRatioNumerator != null || x.voltageTransformerRatioDenominator != null);
    });

    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  toggleModal() {
    this.closeSignal.emit();
  }

  togglePasswordVisibility(field: string): void {
    this.showPassword[field] = !this.showPassword[field];
  }

  onSubmit() {
    try {
      this.submitting = true;

      if (this.deviceForm.invalid) {
        this.deviceForm.markAllAsTouched();
        return;
      }

      if (this.changePasswordOnMeter && this.passwordsMismatch) {
        return;
      }

      const device = new EditMeterDataIn({
        ...this.device,
        assetType: this.deviceForm.get('assetType')?.value,
        serviceLatitude: this.deviceForm.get('serviceLatitude')?.value,
        serviceLongitude: this.deviceForm.get('serviceLongitude')?.value,
        serviceName: this.deviceForm.get('serviceName')?.value,
        serviceNumber: this.deviceForm.get('serviceNumber')?.value,
        serviceType: this.deviceForm.get('serviceType')?.value,
        regionId: this.deviceForm.get('region')?.value,
        areaId: this.deviceForm.get('area')?.value,
        address: this.deviceForm.get('address')?.value,
        meterNumber: this.deviceForm.get('meterNumber')?.value,
        meterMake: this.deviceForm.get('meterMake')?.value,
        meterModel: this.deviceForm.get('meterModel')?.value,
        meterType: this.deviceForm.get('meterType')?.value,
        currentTransformerRatio: (this.deviceForm.get('currentTransformerRatioNumerator')?.value != null && this.deviceForm.get('currentTransformerRatioDenominator')?.value != null) ? new RatioDataIn({
          numerator: this.deviceForm.get('currentTransformerRatioNumerator')?.value,
          denominator: this.deviceForm.get('currentTransformerRatioDenominator')?.value,
        }) : undefined,
        voltageTransformerRatio: (this.deviceForm.get('voltageTransformerRatioNumerator')?.value != null && this.deviceForm.get('voltageTransformerRatioDenominator')?.value != null) ? new RatioDataIn({
          numerator: this.deviceForm.get('voltageTransformerRatioNumerator')?.value,
          denominator: this.deviceForm.get('voltageTransformerRatioDenominator')?.value,
        }) : undefined,
        gsmProvider: this.deviceForm.get('gsmProvider')?.value,
        cellularSimSerialNumber: this.deviceForm.get('cellularSimSerialNumber')?.value,
        meterIp: this.deviceForm.get('meterIp')?.value,
        meterPort: this.deviceForm.get('meterPort')?.value,
        dataCollectionInterval: this.deviceForm.get('dataCollectionInterval')?.value,
        clockTolerance: this.deviceForm.get('clockOffset')?.value,
        meterTimeZone: this.deviceForm.get('meterTimeZone')?.value,
        meterInstallationDate: this.deviceForm.get('meterInstallationDate')?.value,
        meterManufactureDate: this.deviceForm.get('meterManufactureDate')?.value,
        blockCipherKey: this.deviceForm.get('blockCipherKey')?.value,
        authenticationKey: this.deviceForm.get('authenticationKey')?.value,
        firmwareUpdateStatus: undefined,
        password: this.deviceForm.get('password')?.value,
        newPassword: this.changePasswordOnMeter ? (this.deviceForm.get('newPassword')?.value || undefined) : undefined,
        parentId: this.deviceForm.get('parent')?.value,
      } as IEditMeterDataIn);

      this.client.editDevice(device).subscribe({
        next: () => {
          this.toastr.success('Successfully saved.');
          this.closeSignal.emit();
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });

    } finally {
      this.submitted = true;
      this.submitting = false;
    }
  }

  getRegions() {
    this.client.getAllRegionsForOptions().subscribe({
      next: (response) => {
        this.regions = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getMeterModel() {
    this.client.getMeterModel(
      this.deviceForm.get('meterModel')?.value
    ).subscribe({
      next: (response) => {
        this.deviceForm.patchValue({
          meterInterface: response.interface,
          autentificationType: response.authorizationLevel,
          clientAddress: response.clientAddress,
          security: response.security,
          meterPhase: response.meterPhase,
        });
        this.onAuthTypeChange();
      },
      error: (err) => {
        this.toastr.error('Error occured' + err.message);
      },
    });
  }

  getAssetTypes() {
    this.client.getAllAssetTypesForOptions(undefined).subscribe({
      next: (response) => {
        this.assetTypes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getAreas() {
    this.client.getAllAreaByRegionForOptions(this.deviceForm.get('region')?.value).subscribe({
      next: (response) => {
        this.areas = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getServiceTypes() {
    this.client.getAllServiceTypesForOptions().subscribe({
      next: (response) => {
        this.serviceTypes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getMeterMakes() {
    this.client.getAllMeterMakersForSetupOptions().subscribe({
      next: (response) => {
        this.meterMakes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getMeterModels() {
    if (this.deviceForm.get('meterMake')?.value != null) {
      this.client.getAllMeterModelForOptions(this.deviceForm.get('meterMake')?.value).subscribe({
        next: (response) => {
          this.meterModels = response ?? [];
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    } else {
      this.meterModels = [];
    }
  }

  setInputDate(event: string | number | Date): Date {
    const inputDate = new Date(event);
    return inputDate;
  }

  getAreasByRegion(event: Event) {
    const selectedElement = event.target as HTMLSelectElement;
    const selectedValue = selectedElement.value;

    this.deviceForm.patchValue(
      {
        region: selectedValue,
        area: '',
      }
    );

    this.client.getAllAreaByRegionForOptions(selectedValue).subscribe({
      next: (response) => {
        this.areas = response ?? [];
        if (this.areas.length > 0) {
          this.deviceForm.patchValue({ area: this.areas[0].value });
        }
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  onDateRangeChanged(input: DateRangeInput): void {
    if (this.meterDataLocked) return;
    const startDate = new Date(input.dateFrom);
    // Mark dirty before patching so the valueChanges cycle treats this as a meter-data edit.
    this.deviceForm.get('meterInstallationDate')?.markAsDirty();
    this.deviceForm.patchValue({
      meterInstallationDate: toUtcDate(startDate)
    });
    this.toggleDatepicker();
  }

  get formattedMeterInstallationDate(): string {
    const v = this.deviceForm.get('meterInstallationDate')?.value;
    return v ? formatDateDDMMMYYYY(v) : '';
  }

  get formattedMeterManufactureDate(): string {
    const v = this.deviceForm.get('meterManufactureDate')?.value;
    return v ? formatDateDDMMMYYYY(v) : '';
  }

  toggleDatepicker() {
    if (this.meterDataLocked) return;
    this.isDatepickerShown = !this.isDatepickerShown;
  }

  toggleManufactureDatepicker() {
    if (this.meterDataLocked) return;
    this.isManufactureDatepickerShown = !this.isManufactureDatepickerShown;
  }

  clearManufactureDate() {
    if (this.meterDataLocked) return;
    this.deviceForm.get('meterManufactureDate')?.markAsDirty();
    this.deviceForm.patchValue({ meterManufactureDate: null });
  }

  onManufactureDateChanged(input: DateRangeInput): void {
    if (this.meterDataLocked) return;
    const startDate = new Date(input.dateFrom);
    this.deviceForm.get('meterManufactureDate')?.markAsDirty();
    this.deviceForm.patchValue({
      meterManufactureDate: toUtcDate(startDate)
    });
    this.toggleManufactureDatepicker();
  }

  formatInterval(minutes: number): string {
    if (minutes >= 60) {
      const hours = minutes / 60;
      return `${hours}h`;
    } else {
      return `${minutes}mins`;
    }
  }

  getIpFromGsmProvider() {
    if (this.deviceForm.get('cellularSimSerialNumber')?.value) {
      this.client.getIpAddressFromGsmProvider(this.deviceForm.get('gsmProvider')?.value, this.deviceForm.get('cellularSimSerialNumber')?.value).subscribe({
        next: (response) => {
          this.deviceForm.patchValue({ meterIp: response });
        },
        error: (err) => {
          this.toastr.error(err);
          this.deviceForm.patchValue({ meterIp: "" });
        },
      });
    }
  }

  formErrorsExists(controlName: string): boolean {
    const control = this.deviceForm.get(controlName);
    return !!(control && control.invalid && (control.touched || this.submitted));
  }

  getParent(id: string | undefined) {
    if (id == undefined)
      return;
    this.client.getParent(id).subscribe({
      next: (response) => {
        this.selectedParent = response;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  onAssetTypeSelected(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedAssetType = value;
    const parentControl = this.deviceForm.get('parent');
    parentControl?.setValue(null);
    parentControl?.markAsPristine();
    parentControl?.markAsUntouched();
    this.selectedParent = null;
  }

  onParentSelected(value: ParentMeterDataOut | null): void {
    this.selectedParent = value;

    if (value == null)
      this.deviceForm.get('parent')?.setValue(null);
    else
      this.deviceForm.get('parent')?.setValue(value.id);
  }

  fetchParents = (search: string): Observable<GuidParentMeterDataOutDictionaryItem[]> => {
    const request = new MeterParentGetDataIn();
    request.assetTypeId = this.selectedAssetType;
    request.search = search;
    return this.client.getParentsForOption(request);
  }

  onAuthTypeChange() {
    const isLow = this.deviceForm.get('autentificationType')?.value === this.MeterAutentification.Low;
    const passwordCtrl = this.deviceForm.get('password');
    if (isLow) {
      passwordCtrl?.enable({ emitEvent: false });
    } else {
      if (this.changePasswordOnMeter) {
        this.changePasswordOnMeter = false;
        ['newPassword', 'repeatNewPassword'].forEach(name => {
          const ctrl = this.deviceForm.get(name);
          ctrl?.removeValidators(Validators.required);
          ctrl?.setValue(null, { emitEvent: false });
          ctrl?.disable({ emitEvent: false });
        });
        this.lockMeterDataFields(false);
      }
      this.deviceForm.patchValue({ password: undefined }, { emitEvent: false });
      passwordCtrl?.removeValidators(Validators.required);
      passwordCtrl?.disable({ emitEvent: false });
    }
  }

  // ResponseErrorCode.Meter_NotAvailable. The API serializes this enum as its
  // numeric value (2004), not the string name the generated client declares.
  private static readonly METER_NOT_AVAILABLE_CODE = 2004;

  onChangePasswordCheckboxToggle(): void {
    const passwordCtrl = this.deviceForm.get('password');
    if (this.changePasswordOnMeter) {
      passwordCtrl?.addValidators(Validators.required);
      passwordCtrl?.updateValueAndValidity({ emitEvent: false });
      ['newPassword', 'repeatNewPassword'].forEach(name => {
        const ctrl = this.deviceForm.get(name);
        ctrl?.addValidators(Validators.required);
        ctrl?.enable({ emitEvent: false });
        ctrl?.updateValueAndValidity({ emitEvent: false });
      });
      this.lockMeterDataFields(true);
    } else {
      passwordCtrl?.removeValidators(Validators.required);
      passwordCtrl?.updateValueAndValidity({ emitEvent: false });
      ['newPassword', 'repeatNewPassword'].forEach(name => {
        const ctrl = this.deviceForm.get(name);
        ctrl?.removeValidators(Validators.required);
        ctrl?.setValue(null, { emitEvent: false });
        ctrl?.disable({ emitEvent: false });
      });
      this.lockMeterDataFields(false);
    }
  }

  // All editable fields above the password field (everything except the password-change controls).
  private get meterDataControlNames(): string[] {
    return Object.keys(this.deviceForm.controls).filter(name => !this.passwordControlNames.includes(name));
  }

  private isMeterDataDirty(): boolean {
    return this.meterDataControlNames.some(name => {
      const control = this.deviceForm.get(name);
      return !!control && control.enabled && control.dirty;
    });
  }

  // Enforce the mutual exclusion between editing meter data and changing the password on meter.
  private applyEditExclusivity(): void {
    const meterDataDirty = this.isMeterDataDirty();

    // Editing meter data unchecks the password-on-meter checkbox and resets those fields.
    if (meterDataDirty && this.changePasswordOnMeter) {
      this.changePasswordOnMeter = false;
      const passwordCtrl = this.deviceForm.get('password');
      passwordCtrl?.removeValidators(Validators.required);
      passwordCtrl?.updateValueAndValidity({ emitEvent: false });
      ['newPassword', 'repeatNewPassword'].forEach(name => {
        const ctrl = this.deviceForm.get(name);
        ctrl?.removeValidators(Validators.required);
        ctrl?.setValue(null, { emitEvent: false });
        ctrl?.disable({ emitEvent: false });
      });
    }
  }

  private lockMeterDataFields(lock: boolean): void {
    if (lock === this.meterDataLocked) return;

    if (lock) {
      // Snapshot only the currently-enabled controls so design-disabled ones stay disabled on unlock.
      this.lockedMeterDataControls = this.meterDataControlNames.filter(name => this.deviceForm.get(name)?.enabled);
      this.lockedMeterDataControls.forEach(name => this.deviceForm.get(name)?.disable({ emitEvent: false }));
    } else {
      this.lockedMeterDataControls.forEach(name => this.deviceForm.get(name)?.enable({ emitEvent: false }));
      this.lockedMeterDataControls = [];
    }
    this.meterDataLocked = lock;
  }

  onNewPasswordChange() {
    const repeat = this.deviceForm.get('repeatNewPassword');
    if (this.deviceForm.get('newPassword')?.value) {
      if (repeat?.disabled) {
        repeat.enable({ emitEvent: false });
      }
    } else if (repeat?.enabled) {
      repeat.setValue(null, { emitEvent: false });
      repeat.disable({ emitEvent: false });
    }
  }

  get newPasswordEntered(): boolean {
    return !!this.deviceForm.get('newPassword')?.value;
  }

  // Frontend-only check; repeatNewPassword is never sent to the backend.
  get repeatPasswordState(): 'prompt' | 'match' | 'mismatch' {
    const newPassword = this.deviceForm.get('newPassword')?.value;
    const repeat = this.deviceForm.get('repeatNewPassword')?.value;
    if (!repeat) return 'prompt';
    return newPassword === repeat ? 'match' : 'mismatch';
  }

  get passwordsMismatch(): boolean {
    return this.newPasswordEntered && this.repeatPasswordState !== 'match';
  }

  enableRequiredValidatorsForKeys(keys: string[], shouldEnable: boolean) {
    keys.forEach((key) => {
      const control = this.deviceForm.get(key);
      if (control) {
        if (!shouldEnable) {
          control.removeValidators(Validators.required)
        } else {
          control.addValidators([Validators.required]);
        }
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }
}
